import {
  DEFAULT_BAR_SPACING_PX,
  DEFAULT_RIGHT_OFFSET_BARS,
  MIN_BAR_SPACING_PX,
  MAX_BAR_SPACING_PX,
  MIN_VISIBLE_BARS,
} from "../constants";

// x of a bar is its centre, half a bar from its edges
const HALF_BAR = 0.5;
// The empty space right of the last bar takes at most this part of the pane
const MAX_RIGHT_EMPTY_FRACTION = 1 / 3;

const clamp = (value, min, max) => Math.min(max, Math.max(min, value));

/**
 * Horizontal scale: bar index ↔ x in CSS pixels of the pane.
 * The view is defined by the bar spacing and by how many bar widths the
 * right edge stands past the last bar, so x = index * barSpacing + offset.
 * While the view follows real time (motion/viewFollow.js) the phase moves
 * the bars left evenly; the position lastIndex + phase is continuous across
 * a new bar (the index grows by 1, the phase drops by 1).
 */
export default class TimeScale {
  constructor() {
    this.width = 0;
    this.lastIndex = -1;
    this.barSpacing = DEFAULT_BAR_SPACING_PX;
    this.rightOffset = DEFAULT_RIGHT_OFFSET_BARS;
    // Share of the last bar's interval that has passed (> 1 while the next bar is late)
    this.phase = 0;
  }

  setWidth(width) {
    this.width = width;
  }

  setLastIndex(lastIndex) {
    this.lastIndex = lastIndex;
  }

  /** @param {number} phase - ≥ 0, see the class comment */
  setPhase(phase) {
    this.phase = phase;
  }

  /**
   * @param {{ barSpacing: number, rightOffset: number }} view - CSS px, bars
   */
  setView({ barSpacing, rightOffset }) {
    this.barSpacing = barSpacing;
    this.rightOffset = rightOffset;
  }

  /** The centre of the last bar is not right of the pane. */
  isLastBarVisible() {
    return this.rightOffset + this.phase + HALF_BAR >= 0;
  }

  /**
   * A bar was added on the right (what the view does: resolveNewBarScroll).
   *
   * @param {boolean} isFollowing - the view follows, so the new bar is where the
   *   last one was; otherwise it stays on the same bars
   */
  appendBar(isFollowing) {
    if (!isFollowing) this.rightOffset -= 1;

    this.lastIndex += 1;
  }

  /** Brings the latest bars back with the default right offset when they are out of view; zoom stays. */
  showLastBar() {
    if (!this.isLastBarVisible()) this.rightOffset = DEFAULT_RIGHT_OFFSET_BARS;
  }

  /** Default zoom and right offset, as after the first load (double click on the time axis). */
  resetView() {
    this.barSpacing = DEFAULT_BAR_SPACING_PX;
    this.rightOffset = DEFAULT_RIGHT_OFFSET_BARS;
  }

  /**
   * Moves the bars right by `pixels` (left when negative). Stops when only
   * MIN_VISIBLE_BARS are left in view on the left, and when the empty space
   * right of the last bar reaches MAX_RIGHT_EMPTY_FRACTION of the pane.
   *
   * @param {number} pixels - CSS px
   * @returns {boolean} false when the view did not move (an edge is reached)
   */
  scrollBy(pixels) {
    const previous = this.rightOffset;

    this.rightOffset = this.clampRightOffset(this.rightOffset - pixels / this.barSpacing);

    return this.rightOffset !== previous;
  }

  /**
   * Zooms so that the bar now at `x` stays at `x`.
   *
   * @param {number} x - CSS px of the pane
   * @param {number} barSpacing - wanted spacing, clamped to the zoom limits
   */
  zoomAt(x, barSpacing) {
    this.placeIndexAt(this.xToIndex(x), x, barSpacing);
  }

  /**
   * Sets the zoom and puts a (fractional) bar index at `x`: pinch keeps the
   * bar between the fingers under them.
   *
   * @param {number} index
   * @param {number} x - CSS px of the pane
   * @param {number} barSpacing - wanted spacing, clamped to the zoom limits
   */
  placeIndexAt(index, x, barSpacing) {
    this.barSpacing = this.clampBarSpacing(barSpacing);
    this.rightOffset = this.clampRightOffset(
      (this.width - x) / this.barSpacing + index - this.lastIndex - this.phase - HALF_BAR,
    );
  }

  // Zoom limits: MIN_BAR_SPACING_PX … MAX_BAR_SPACING_PX
  clampBarSpacing(barSpacing) {
    return clamp(barSpacing, MIN_BAR_SPACING_PX, MAX_BAR_SPACING_PX);
  }

  // The first bar stays MIN_VISIBLE_BARS from the right edge; right of the last bar
  // at most MAX_RIGHT_EMPTY_FRACTION of the pane is empty; `barSpacing` is the current one by default
  clampRightOffset(rightOffset, barSpacing = this.barSpacing) {
    const min = MIN_VISIBLE_BARS - 1 - this.lastIndex - this.phase;
    const max = (this.width * MAX_RIGHT_EMPTY_FRACTION) / barSpacing - HALF_BAR;

    return clamp(rightOffset, min, Math.max(min, max));
  }

  /** x of bar index 0; together with barSpacing it is the transform for WebGL. */
  get offset() {
    return this.width - (this.lastIndex + this.phase + this.rightOffset + HALF_BAR) * this.barSpacing;
  }

  indexToX(index) {
    return index * this.barSpacing + this.offset;
  }

  xToIndex(x) {
    return (x - this.offset) / this.barSpacing;
  }

  /**
   * Indexes of bars at least partly inside the pane.
   *
   * @param {number} barCount
   * @returns {{ first: number, last: number }} empty when first > last
   */
  visibleRange(barCount) {
    return {
      first: Math.max(0, Math.floor(this.xToIndex(0))),
      last: Math.min(barCount - 1, Math.ceil(this.xToIndex(this.width))),
    };
  }
}
